import { SectionHeader } from '../common/section-header'
import { Badge, Notice, useToast } from '@speaking-coach/ui'
import { personLabel } from '../common/person-label.js'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useAtomValue, useSetAtom } from 'jotai'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { FormalNameFields } from '../common/identity-fields.js'
import { SaveButton } from '../common/save-button.js'
import { UnsavedChangesGuard } from '../common/unsaved-changes.js'
import { editCoooriAdminSchema, type EditCoooriAdminForm } from './admin-schema.js'
import { useNavigate, useParams } from 'react-router-dom'

import { queryKeys } from '../api/query-keys.js'
import { DismissibleAlert } from '../common/dismissible-alert.js'
import { HighConsequenceActionPanel } from '../common/high-consequence-action-panel.js'
import {
  OPERATOR_DETAIL_PAGE,
  OPERATOR_PAGE_MAIN,
} from '../common/page-layout.js'
import { PageHeader } from '../components/page-header.js'
import { UndeliverableNotice } from '../email_suppressions/undeliverable-notice.js'
import { adminUserAtom } from '../state/atoms.js'
import {
  AdminActionControls,
  DeactivateAdminControl,
} from './admin-action-controls.js'
import { AdminAccessState } from './admin-access-state.js'
import { coooriAdminsApi, messageForCoooriAdminError } from './api.js'
import { formatAdminDate } from './format.js'
import type { CoooriAdminDetail } from './types.js'
import { HistoryLink } from '../activity/history-link.js'

export function CoooriAdminDetailPage(): JSX.Element {
  const { userId = '' } = useParams()
  const currentAdmin = useAtomValue(adminUserAtom)
  const queryClient = useQueryClient()
  const navigate = useNavigate()
  const { toast } = useToast()
  const detail = useQuery({
    queryKey: queryKeys.coooriAdmin(userId),
    queryFn: () => coooriAdminsApi.get(userId),
  })
  const resend = useMutation({
    mutationFn: () => coooriAdminsApi.resendInvite(userId),
    onSuccess: (updated) => {
      queryClient.setQueryData<CoooriAdminDetail>(
        queryKeys.coooriAdmin(userId),
        (current) =>
          current === undefined ? current : { ...current, ...updated },
      )
      void queryClient.invalidateQueries({ queryKey: queryKeys.coooriAdminLists })
      toast({ variant: 'success', description: `New invitation queued for ${updated.name}.` })
    },
  })
  const cancel = useMutation({
    mutationFn: () => coooriAdminsApi.cancelInvite(userId),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.coooriAdminLists })
      toast({ variant: 'success', description: `Cancelled the invitation for ${detail.data?.name ?? 'this admin'}.` })
      navigate('/admin/admins', { replace: true })
    },
  })
  const deactivate = useMutation({
    mutationFn: (reason: string) => coooriAdminsApi.deactivate(userId, { reason }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.coooriAdminLists })
      toast({ variant: 'success', description: `Deactivated access for ${detail.data?.name ?? 'this admin'}.` })
      navigate('/admin/admins', { replace: true })
    },
  })
  const invitationActionError = resend.error ?? cancel.error
  const admin = detail.data

  return (
    <main className={OPERATOR_PAGE_MAIN}>
      <div className={OPERATOR_DETAIL_PAGE}>
        {detail.isPending && (
          <p className="mt-4 text-text-secondary">Loading Cooori admin…</p>
        )}
        {detail.isError && (
          <Notice variant="danger">
            <strong>Could not load this Cooori admin.</strong>{' '}
            {messageForCoooriAdminError(detail.error)}
          </Notice>
        )}
        {admin !== undefined && (
          <>
            <PageHeader
              title={personLabel(admin) ?? 'Admin'}
              meta={
                admin.userId === currentAdmin?.id ? (
                  <Badge variant="neutral">You</Badge>
                ) : undefined
              }
              action={<HistoryLink to={`/admin/admins/${userId}/history`} />}
            />

            {admin.emailUndeliverable !== null && (
              <div className="mb-6">
                <UndeliverableNotice value={admin.emailUndeliverable} />
              </div>
            )}
            <DismissibleAlert
              className="mb-6"
              message={
                invitationActionError === null
                  ? null
                  : messageForCoooriAdminError(invitationActionError)
              }
              onDismiss={() => {
                resend.reset()
                cancel.reset()
              }}
            />

            <div className="space-y-4">
              <section
                aria-label="Access and invitation"
                className="rounded-token-card bg-surface-card p-6"
              >
                <SectionHeader
                  title="Access and invitation"
                  actions={(admin.canResend || admin.canCancel) && (
                    <AdminActionControls
                      admin={admin}
                      resendPending={resend.isPending}
                      cancelPending={cancel.isPending}
                      onResend={() => resend.mutate()}
                      onCancel={() => cancel.mutate()}
                    />
                  )}
                />
                <div className="mt-4"><AdminAccessState admin={admin} /></div>
                {admin.lastDelivery !== null && (
                  <dl className="mt-5 grid gap-x-8 gap-y-3 text-body sm:grid-cols-2">
                    <div>
                      <dt className="text-text-secondary">Invitation scheduled</dt>
                      <dd>{formatAdminDate(admin.lastDelivery.scheduledFor)}</dd>
                    </div>
                    <div>
                      <dt className="text-text-secondary">Sent</dt>
                      <dd>{formatAdminDate(admin.lastDelivery.sentAt)}</dd>
                    </div>
                    {admin.lastDelivery.failedAt !== null && (
                      <div>
                        <dt className="text-text-secondary">Failed</dt>
                        <dd>{formatAdminDate(admin.lastDelivery.failedAt)}</dd>
                      </div>
                    )}
                    {admin.lastDelivery.terminalAt !== null && (
                      <div>
                        <dt className="text-text-secondary">Closed</dt>
                        <dd>{formatAdminDate(admin.lastDelivery.terminalAt)}</dd>
                      </div>
                    )}
                  </dl>
                )}
              </section>

              <section aria-label="Identity details" className="rounded-token-card bg-surface-card p-6">
                <SectionHeader title="Identity details" />
                <dl className="mt-4 grid gap-x-8 gap-y-3 text-body sm:grid-cols-2">
                  <div>
                    <dt className="text-text-secondary">Email</dt>
                    <dd>{admin.email}</dd>
                  </div>
                  <div>
                    <dt className="text-text-secondary">Created</dt>
                    <dd>{formatAdminDate(admin.createdAt)}</dd>
                  </div>
                </dl>
                {admin.canEdit ? (
                  <AdminIdentityForm key={admin.userId} admin={admin} />
                ) : (
                  <dl className="mt-4 grid gap-4 text-body sm:grid-cols-2">
                    <div>
                      <dt className="text-text-secondary">
                        Family name
                      </dt>
                      <dd>{admin.lastName ?? 'Not recorded'}</dd>
                    </div>
                    <div>
                      <dt className="text-text-secondary">
                        Given name
                      </dt>
                      <dd>{admin.firstName ?? 'Not recorded'}</dd>
                    </div>
                  </dl>
                )}
              </section>

              {admin.canDeactivate && (
                <HighConsequenceActionPanel
                  dangerZone
                  title="Deactivate admin access"
                  description="Use this when the person no longer needs Admin. They will be signed out immediately, and their password and recovery links will stop working. Audit history remains."
                  error={
                    deactivate.error === null
                      ? undefined
                      : messageForCoooriAdminError(deactivate.error)
                  }
                  onErrorDismiss={() => deactivate.reset()}
                >
                  <DeactivateAdminControl
                    admin={admin}
                    pending={deactivate.isPending}
                    onDeactivate={(reason) => deactivate.mutate(reason)}
                  />
                </HighConsequenceActionPanel>
              )}
            </div>
          </>
        )}
      </div>
    </main>
  )
}

function AdminIdentityForm({ admin }: { admin: CoooriAdminDetail }): JSX.Element {
  const queryClient = useQueryClient()
  const setCurrentAdmin = useSetAtom(adminUserAtom)
  const form = useForm<EditCoooriAdminForm>({
    resolver: zodResolver(editCoooriAdminSchema),
    defaultValues: { firstName: admin.firstName ?? '', lastName: admin.lastName ?? '' },
  })
  const update = useMutation({
    mutationFn: (values: EditCoooriAdminForm) => coooriAdminsApi.update(admin.userId, values),
    onSuccess: (updated) => {
      queryClient.setQueryData(queryKeys.coooriAdmin(admin.userId), updated)
      void queryClient.invalidateQueries({ queryKey: queryKeys.coooriAdminLists })
      setCurrentAdmin((current) => current?.id === updated.userId
        ? { ...current, name: updated.name }
        : current)
      form.reset({ firstName: updated.firstName ?? '', lastName: updated.lastName ?? '' })
    },
  })
  return (
    <form
      aria-label="Edit Cooori admin identity"
      className="mt-4 space-y-4"
      noValidate
      onSubmit={form.handleSubmit((values) => {
        if (!update.isPending && form.formState.isDirty) update.mutate(values)
      })}
    >
      <UnsavedChangesGuard when={form.formState.isDirty} name={`Cooori admin ${personLabel(admin) ?? 'Admin'}`} />
      {update.error && <Notice variant="danger">{messageForCoooriAdminError(update.error)}</Notice>}
      <FormalNameFields
        firstName={{ ...form.register('firstName'), disabled: update.isPending }}
        lastName={{ ...form.register('lastName'), disabled: update.isPending }}
        errors={form.formState.errors}
      />
      <SaveButton pending={update.isPending} dirty={form.formState.isDirty} justSaved={update.isSuccess} />
    </form>
  )
}
